import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Search, UserPlus, UserCheck, Users, ShieldAlert, Star } from 'lucide-react';
import { t } from '../../utils/i18n';
import { useDmPersona } from '../../context/DmPersonaContext';
import { useApiEnvironment } from '../../context/ApiEnvironmentContext';
import { API_ENV_TEST } from '../../utils/apiEnvironment';
import { searchPartnerCelebs, followCeleb, unfollowCeleb } from '../../api/celebFollow';
import UserAvatar from '../../components/UserAvatar';

const PAGE_SIZE = 20;

export default function CelebFollow() {
  const { accessToken, userId, username } = useDmPersona();
  const { apiEnvironment, setApiEnvironment } = useApiEnvironment();
  const isTestEnv = apiEnvironment === API_ENV_TEST;

  const [queryInput, setQueryInput] = useState('');
  const [submittedQuery, setSubmittedQuery] = useState('');
  const queryClient = useQueryClient();
  const [pending, setPending] = useState({});
  const [actionError, setActionError] = useState(null);

  const query = useInfiniteQuery({
    queryKey: ['celeb-follow', apiEnvironment, userId, submittedQuery],
    queryFn: ({ pageParam = 0 }) =>
      searchPartnerCelebs({ query: submittedQuery, page: pageParam, size: PAGE_SIZE }, accessToken),
    getNextPageParam: (last) => {
      const loaded = (last.page + 1) * last.size;
      return loaded < last.total ? last.page + 1 : undefined;
    },
    initialPageParam: 0,
    enabled: isTestEnv && !!accessToken,
    staleTime: 30 * 1000,
  });

  const items = useMemo(
    () =>
      (query.data?.pages || [])
        .flatMap((p) => p.items)
        // 자기 자신(현재 페르소나)은 목록에서 제외
        .filter((celeb) => String(celeb.user_id) !== String(userId)),
    [query.data, userId]
  );

  const effective = (celeb) => {
    return {
      // is_fan is target -> caller, never a fallback for caller -> target.
      isFollowing: typeof celeb.is_following === 'boolean' ? celeb.is_following : null,
      fanCount: Number(celeb.fan_count || 0),
    };
  };

  const handleToggle = async (celeb) => {
    const uid = celeb.user_id;
    if (pending[uid]) return;
    const cur = effective(celeb);
    if (cur.isFollowing === null) return;
    const reconcile = async (isFollowing, fanCount) => {
      const queryKey = ['celeb-follow', apiEnvironment, userId];
      queryClient.setQueriesData({ queryKey }, (data) => data && ({
        ...data,
        pages: data.pages.map((page) => ({
          ...page,
          items: page.items.map((item) => String(item.user_id) === String(uid)
            ? { ...item, is_following: isFollowing, fan_count: fanCount } : item),
        })),
      }));
      await queryClient.invalidateQueries({ queryKey });
    };
    setActionError(null);
    setPending((p) => ({ ...p, [uid]: true }));
    try {
      if (cur.isFollowing) {
        await unfollowCeleb(uid, accessToken);
        await reconcile(false, Math.max(0, cur.fanCount - 1));
      } else {
        await followCeleb(uid, accessToken);
        await reconcile(true, cur.fanCount + 1);
      }
    } catch (e) {
      // 409 already-following / 404 not-following → reconcile to the intended state
      if (e?.status === 409 && !cur.isFollowing) {
        await reconcile(true, cur.fanCount);
      } else if (e?.status === 404 && cur.isFollowing) {
        await reconcile(false, cur.fanCount);
      } else {
        setActionError(e?.message || t('celebRoom.follow.errors.actionFailed'));
      }
    } finally {
      setPending((p) => ({ ...p, [uid]: false }));
    }
  };

  const onSearch = (e) => {
    e.preventDefault();
    setSubmittedQuery(queryInput.trim());
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
      <Link
        to="/celeb-room/feed"
        className="inline-flex items-center gap-2 text-sm text-dark-text-secondary hover:text-primary mb-6"
      >
        <ArrowLeft size={16} />
        {t('celebRoom.follow.backToFeed')}
      </Link>

      <div className="flex items-center justify-between gap-2 mb-2">
        <h1 className="text-xl sm:text-2xl font-bold text-white flex items-center gap-2">
          <Star size={20} className="text-primary" />
          {t('celebRoom.follow.title')}
          <span className="text-sm sm:text-base font-normal text-dark-text-secondary">
            {t('celebRoom.follow.globalFeed')}
          </span>
        </h1>
        <span className="text-xs px-2 py-0.5 rounded-full bg-primary/15 text-primary border border-primary/40">
          TEST
        </span>
      </div>
      <p className="text-sm text-dark-text-secondary mb-6">
        {username
          ? t('celebRoom.follow.actingAs', { username, userId })
          : t('celebRoom.follow.description')}
      </p>

      {!isTestEnv ? (
        <div className="text-center py-12 px-6 rounded-2xl bg-dark-card border border-dark-border">
          <ShieldAlert size={32} className="mx-auto mb-3 text-dark-text-muted" />
          <p className="text-sm text-dark-text-secondary mb-5">{t('celebRoom.follow.testOnly')}</p>
          <button
            type="button"
            onClick={() => setApiEnvironment(API_ENV_TEST)}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-primary text-black text-sm font-bold hover:bg-primary/90 transition-colors"
          >
            {t('celebRoom.follow.switchToTest')}
          </button>
        </div>
      ) : (
        <>
          <form onSubmit={onSearch} className="flex items-center gap-2 mb-5">
            <div className="relative flex-grow">
              <Search
                size={16}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-dark-text-muted"
              />
              <input
                type="text"
                value={queryInput}
                onChange={(e) => setQueryInput(e.target.value)}
                placeholder={t('celebRoom.follow.searchPlaceholder')}
                className="w-full bg-dark-surface border border-dark-border rounded-lg pl-9 pr-3 py-2 text-sm text-white placeholder-dark-text-muted focus:outline-none focus:ring-2 focus:ring-primary/50"
              />
            </div>
            <button
              type="submit"
              className="px-4 py-2 rounded-lg bg-primary text-black text-sm font-bold hover:bg-primary/90 transition-colors"
            >
              {t('celebRoom.follow.search')}
            </button>
          </form>

          {actionError && (
            <div className="mb-4 p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-300 text-sm">
              {actionError}
            </div>
          )}

          {query.isLoading ? (
            <div className="text-center py-16 text-dark-text-secondary">{t('common.loading')}</div>
          ) : query.isError ? (
            <div className="text-center py-12 px-4 rounded-xl bg-red-500/10 border border-red-500/30 text-red-300 text-sm">
              {query.error?.message || t('celebRoom.follow.errors.loadFailed')}
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-16 px-6 rounded-2xl bg-dark-card border border-dark-border">
              <Users size={32} className="mx-auto mb-3 text-dark-text-muted" />
              <p className="text-sm text-dark-text-secondary">{t('celebRoom.follow.empty')}</p>
            </div>
          ) : (
            <ul className="space-y-2">
              {items.map((celeb) => {
                const { isFollowing, fanCount } = effective(celeb);
                const busy = Boolean(pending[celeb.user_id]);
                return (
                  <li
                    key={celeb.user_id}
                    className="flex items-center gap-3 p-3 rounded-xl bg-dark-card border border-dark-border"
                  >
                    <UserAvatar user={celeb} size={44} className="w-11 h-11" />
                    <div className="min-w-0 flex-grow">
                      <p className="text-sm font-semibold text-white truncate">
                        {celeb.name || celeb.username || `#${celeb.user_id}`}
                        {celeb.username && (
                          <span className="ml-1.5 text-xs font-normal text-dark-text-muted">
                            @{celeb.username}
                          </span>
                        )}
                      </p>
                      <p className="text-[11px] text-dark-text-muted flex items-center gap-1">
                        <Users size={11} /> {fanCount}
                        <span className="ml-1 text-dark-text-secondary">#{celeb.user_id}</span>
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleToggle(celeb)}
                      disabled={busy || isFollowing === null}
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-colors disabled:opacity-50 ${
                        isFollowing
                          ? 'bg-dark-surface border border-dark-border text-dark-text-secondary hover:border-red-500/40 hover:text-red-300'
                          : 'bg-primary text-black hover:bg-primary/90'
                      }`}
                    >
                      {isFollowing ? <UserCheck size={14} /> : <UserPlus size={14} />}
                      {busy
                        ? t('common.loading')
                        : isFollowing === null
                          ? t('celebRoom.follow.statusUnavailable')
                        : isFollowing
                          ? t('celebRoom.follow.following')
                          : t('celebRoom.follow.follow')}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}

          {query.hasNextPage && (
            <div className="mt-6 text-center">
              <button
                type="button"
                onClick={() => query.fetchNextPage()}
                disabled={query.isFetchingNextPage}
                className="px-4 py-2 rounded-lg bg-dark-surface border border-dark-border text-dark-text-secondary text-sm hover:border-primary/40 hover:text-primary transition-colors disabled:opacity-50"
              >
                {query.isFetchingNextPage ? t('common.loading') : t('celebRoom.follow.loadMore')}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
